import { Box, ButtonBase, Stack, useMediaQuery, useTheme } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import BaseDialog from '../../ui/BaseDialog';
import DrawerBottom from '../../ui/DrawerBottom/DrawerBottom';
import IconCircleWrapper from '../../ui/IconCircleWrapper';
import {
  MinusIcon,
  PlusIcon,
  TransferIcon,
  VerticalAlignBottomIcon,
  VerticalAlignTopIcon,
} from '../../ui/icons';
import { TransactionType } from '../../forms/basic-transactions/components/types';

export type LoanSubType = 'borrow' | 'lend' | 'repay' | 'collect' | 'borrow-expense' | null;
export type CreditCardSubType = 'pay_balance' | 'convert_installment' | 'cash_advance' | null;

export interface TransactionTypeOption {
  type: TransactionType;
  loanType?: LoanSubType;
  creditCardType?: CreditCardSubType;
}

interface TransactionTypeSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelectTransactionType: (option: TransactionTypeOption) => void;
  currentType?: TransactionType;
  currentLoanType?: string | null;
  currentCreditCardType?: string | null;
}

interface TransactionTypeItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  color?: string;
  bgColor: string;
  type: TransactionType;
  loanType?: LoanSubType;
  creditCardType?: CreditCardSubType;
}

interface TransactionTypeGroup {
  title: string;
  items: TransactionTypeItem[];
}

const TransactionTypeSelector = ({
  open,
  onClose,
  onSelectTransactionType,
  currentType,
  currentLoanType,
  currentCreditCardType,
}: TransactionTypeSelectorProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['transactionTypeSelector', 'forms']);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const title = t('title', { ns: 'transactionTypeSelector' });

  // Colors matching the overview page (MoneyInTrending/MoneyOutTrending)
  const transactionTypeGroups: TransactionTypeGroup[] = [
    {
      title: t('groups.basic', { ns: 'transactionTypeSelector', defaultValue: 'Cơ bản' }),
      items: [
        {
          key: 'expense',
          label: t('types.expense', { ns: 'transactionTypeSelector', defaultValue: 'Chi tiêu' }),
          icon: <MinusIcon />,
          bgColor: theme.palette.pink[500], // Spending color from MoneyOutTrending
          type: TransactionType.EXPENSE,
        },
        {
          key: 'income',
          label: t('types.income', { ns: 'transactionTypeSelector', defaultValue: 'Thu nhập' }),
          icon: <PlusIcon />,
          bgColor: theme.palette.teal[500], // Income color from MoneyInTrending
          type: TransactionType.INCOME,
        },
        {
          key: 'transfer',
          label: t('types.transfer', { ns: 'transactionTypeSelector', defaultValue: 'Chuyển khoản' }),
          icon: <TransferIcon />,
          bgColor: theme.palette.grey[500],
          type: TransactionType.TRANSFER,
        },
      ],
    },
    {
      title: t('groups.loan', { ns: 'transactionTypeSelector', defaultValue: 'Vay nợ' }),
      items: [
        {
          key: 'borrow',
          label: t('types.borrow', { ns: 'transactionTypeSelector', defaultValue: 'Vay' }),
          icon: <VerticalAlignBottomIcon />,
          bgColor: theme.palette.red[500], // Borrowing color from MoneyInTrending
          type: TransactionType.LOAN,
          loanType: 'borrow',
        },
        {
          key: 'lend',
          label: t('types.lend', { ns: 'transactionTypeSelector', defaultValue: 'Cho vay' }),
          icon: <VerticalAlignTopIcon />,
          bgColor: theme.palette.green[500], // Lending color from MoneyOutTrending
          type: TransactionType.LOAN,
          loanType: 'lend',
        },
        {
          key: 'repay',
          label: t('types.repay', { ns: 'transactionTypeSelector', defaultValue: 'Trả nợ' }),
          icon: <VerticalAlignTopIcon />,
          bgColor: theme.palette.red[50], // Debt Payment color (adjusted for visibility)
          color: theme.palette.red[500],
          type: TransactionType.LOAN,
          loanType: 'repay',
        },
        {
          key: 'collect',
          label: t('types.collect', { ns: 'transactionTypeSelector', defaultValue: 'Thu nợ' }),
          icon: <VerticalAlignBottomIcon />,
          bgColor: theme.palette.green[50], // Debt Collection color from MoneyInTrending
          color: theme.palette.green[500],
          type: TransactionType.LOAN,
          loanType: 'collect',
        },
        {
          key: 'borrow-expense',
          label: t('types.borrow_expense', { ns: 'transactionTypeSelector', defaultValue: 'Vay chi tiêu' }),
          icon: <VerticalAlignBottomIcon />,
          bgColor: theme.palette.pink[500], // Same as expense color
          type: TransactionType.LOAN,
          loanType: 'borrow-expense',
        },
      ],
    },
    {
      title: t('groups.credit_card', { ns: 'transactionTypeSelector', defaultValue: 'Thẻ tín dụng' }),
      items: [
        {
          key: 'pay_balance',
          label: t('types.pay_balance', { ns: 'transactionTypeSelector', defaultValue: 'Thanh toán dư nợ' }),
          icon: <VerticalAlignBottomIcon />,
          bgColor: theme.palette.purple[500],
          type: TransactionType.CREDIT_CARD,
          creditCardType: 'pay_balance',
        },
        {
          key: 'convert_installment',
          label: t('types.convert_installment', { ns: 'transactionTypeSelector', defaultValue: 'Chuyển đổi trả góp' }),
          icon: <CreditCardIcon />,
          bgColor: theme.palette.purple[500],
          type: TransactionType.CREDIT_CARD,
          creditCardType: 'convert_installment',
        },
        {
          key: 'cash_advance',
          label: t('types.cash_advance', { ns: 'transactionTypeSelector', defaultValue: 'Ứng tiền' }),
          icon: <VerticalAlignTopIcon />,
          bgColor: theme.palette.purple[500],
          type: TransactionType.CREDIT_CARD,
          creditCardType: 'cash_advance',
        },
      ],
    },
  ];

  const handleSelect = (item: TransactionTypeItem) => {
    onSelectTransactionType({
      type: item.type,
      loanType: item.loanType || null,
      creditCardType: item.creditCardType || null,
    });
    onClose();
  };

  const isSelected = (item: TransactionTypeItem) => {
    if (item.type === TransactionType.LOAN) {
      return currentType === item.type && currentLoanType === item.loanType;
    }
    if (item.type === TransactionType.CREDIT_CARD) {
      return currentType === item.type && currentCreditCardType === item.creditCardType;
    }
    return currentType === item.type;
  };

  const renderTransactionTypeItem = (item: TransactionTypeItem) => {
    const selected = isSelected(item);
    const borderColor = item.color || item.bgColor;

    return (
      <ButtonBase
        key={item.key}
        sx={{
          p: 0.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          borderRadius: 2,
          border: selected ? `2px solid ${borderColor}` : '2px solid transparent',
          backgroundColor: selected ? `${borderColor}14` : 'transparent', // 14 = ~8% opacity in hex
          '&:hover': {
            backgroundColor: selected ? `${borderColor}20` : theme.palette.action.hover,
          },
        }}
        onClick={() => handleSelect(item)}
      >
        <IconCircleWrapper
          size={40}
          color={theme.palette.common.white}
          bgcolor={item.bgColor}
          sx={{
            color: item.color || 'white',
          }}
        >
          {item.icon}
        </IconCircleWrapper>
        <Typography
          fontSize={12}
          component="span"
          textAlign="center"
          fontWeight={selected ? 600 : 400}
          sx={{ mt: 1, maxWidth: 70 }}
          color={selected ? borderColor : 'text.primary'}
        >
          {item.label}
        </Typography>
      </ButtonBase>
    );
  };

  const content = (
    <Stack spacing={0}>
      {transactionTypeGroups.map((group, groupIndex) => (
        <Accordion
          key={groupIndex}
          defaultExpanded
          sx={{
            boxShadow: 'none',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 0,
              minHeight: 'auto',
              '& .MuiAccordionSummary-content': {
                margin: '8px 0',
              },
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {group.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, py: 2 }}>
            <Stack
              gap={2}
              justifyContent="flex-start"
              sx={{
                gap: 0,
                display: 'grid',
                gridRowGap: theme.spacing(2),
                gridTemplateColumns: 'repeat(4, 1fr)'
              }}
            >
              {group.items.map(renderTransactionTypeItem)}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );

  const settingsLink = (
    <Box sx={{ py: 2, textAlign: 'center' }}>
      <Typography
        component="span"
        sx={{
          color: theme.palette.text.disabled,
          textDecoration: 'none',
          fontSize: 14,
          cursor: 'not-allowed',
        }}
      >
        {t('set_default', { ns: 'transactionTypeSelector', defaultValue: 'Cài đặt giao dịch mặc định' })}
      </Typography>
    </Box>
  );

  if (isDesktop) {
    return (
      <BaseDialog
        open={open}
        maxWidth="sm"
        onClose={onClose}
        title={title}
      >
        {content}
        {settingsLink}
      </BaseDialog>
    );
  }

  return (
    <DrawerBottom
      mobileKeyboardAware
      open={open}
      onClose={onClose}
      title={title}
    >
      {content}
      {settingsLink}
    </DrawerBottom>
  );
};

export default TransactionTypeSelector;
